import React, { useState, useMemo } from 'react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { usePolling } from '@/hooks/usePolling';
import { evaluationsApi } from '@/services/evaluations.api';
import { Evaluation } from '@/types';
import { formatTimestamp, formatDuration, getScoreClass, truncateText } from '@/utils/formatters';

interface EvaluationRunsProps {
  onSelectRun: (runId: string) => void;
  selectedRunId?: string;
}

interface DateFilter {
  label: string;
  days: number | null; // null means "all time"
}

const DATE_FILTER_OPTIONS: DateFilter[] = [
  { label: 'Past week', days: 7 },
  { label: 'Past month', days: 30 },
  { label: 'Past 3 months', days: 90 },
  { label: 'All time', days: null },
];

export function EvaluationRuns({ onSelectRun, selectedRunId }: EvaluationRunsProps) {
  const [textFilter, setTextFilter] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilter>(DATE_FILTER_OPTIONS[0]); // Default to past week
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data: runsData, refetch, isLoading } = useApi(() => evaluationsApi.list(), {
    refetchInterval: 15000, // Poll every 15 seconds (reduced from 5)
  });

  // Ensure runs is always an array
  const runs = Array.isArray(runsData) ? runsData : [];

  // Filter runs based on text search, date, and status
  const filteredRuns = useMemo(() => {
    let filtered = runs;

    // Apply date filter
    if (selectedDateFilter.days !== null) {
      const cutoffTime = Math.floor(Date.now() / 1000) - (selectedDateFilter.days * 24 * 60 * 60);
      filtered = filtered.filter(run => run.created_at >= cutoffTime);
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(run => run.status === statusFilter);
    }

    // Apply text filter
    if (textFilter) {
      const searchTerm = textFilter.toLowerCase();
      filtered = filtered.filter((run) => 
        run.id.toLowerCase().includes(searchTerm) ||
        run.status.toLowerCase().includes(searchTerm) ||
        run.agent_name?.toLowerCase().includes(searchTerm) ||
        run.evaluation_name?.toLowerCase().includes(searchTerm)
      );
    }

    // Sort by created_at descending (most recent first)
    return filtered.sort((a, b) => b.created_at - a.created_at);
  }, [runs, selectedDateFilter, statusFilter, textFilter]);

  const clearCompleted = () => {
    // This would need to be implemented in the backend
    // For now, just refresh the list
    refetch();
  };

  if (isLoading && runs.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Evaluation Runs</h2>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading evaluations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Evaluation Runs</h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={refetch}
              className="px-3 py-1 text-sm text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
            >
              ↻ Refresh
            </button>
            <button
              onClick={clearCompleted}
              className="px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200"
            >
              Clear Completed
            </button>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          {/* Date Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Time:</label>
            <select
              value={selectedDateFilter.label}
              onChange={(e) => {
                const filter = DATE_FILTER_OPTIONS.find(f => f.label === e.target.value);
                if (filter) setSelectedDateFilter(filter);
              }}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            >
              {DATE_FILTER_OPTIONS.map((option) => (
                <option key={option.label} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="running">Running</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Text Search */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Search:</label>
            <input
              type="text"
              placeholder="Filter by ID, agent, name..."
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              className="px-3 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500 min-w-0"
              style={{ width: '200px' }}
            />
          </div>

          {/* Results count */}
          <div className="text-sm text-gray-500 ml-auto">
            Showing {filteredRuns.length} of {runs.length} runs
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dataset
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cases
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredRuns.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-8 text-center text-gray-500 italic">
                  {textFilter || statusFilter !== 'all' || selectedDateFilter.days !== null 
                    ? 'No evaluation runs match your filters' 
                    : 'No evaluation runs yet'}
                </td>
              </tr>
            ) : (
              filteredRuns.map((run) => {
                const duration = run.completed_at && run.created_at 
                  ? run.completed_at - run.created_at 
                  : null;
                
                const progress = run.status === 'running' 
                  ? `${run.completed_items}/${run.total_items || run.num_cases}` 
                  : run.num_cases?.toString() || '0';

                return (
                  <tr 
                    key={run.id}
                    className={`hover:bg-gray-50 cursor-pointer ${
                      selectedRunId === run.id ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => onSelectRun(run.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="text-sm font-mono text-gray-900" 
                        title={run.id}
                      >
                        {run.id.substring(0, 8)}...
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {run.agent_name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {run.evaluation_name || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className="text-sm text-gray-900"
                        title={run.source_file || 'Direct input'}
                      >
                        {run.source_file 
                          ? run.source_file.split('/').pop() 
                          : 'Manual'
                        }
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={run.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatTimestamp(run.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDuration(duration)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {progress}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {run.overall_score !== undefined && run.overall_score !== null ? (
                        <span className={`text-sm font-semibold ${getScoreClass(run.overall_score)}`}>
                          {run.overall_score.toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onSelectRun(run.id);
                        }}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}